import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X } from "lucide-react";
import FollowBanner from "@/components/blog/FollowBanner";

const LOGO =
  "https://customer-assets.emergentagent.com/job_design-led-builds/artifacts/p4d7ltzn_IMG-20260527-WA0015.jpg";

const links = [
  { href: "/#capabilities", label: "Capabilities" },
  { href: "/#workshop", label: "Workshop" },
  { href: "/#intelligence", label: "Intelligence" },
  { href: "/#sectors", label: "Sectors" },
  { href: "/#process", label: "Process" },
  { href: "/blog", label: "Journal" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const { pathname } = useLocation();
  const isHome = pathname === "/";

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 16);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const linkCls = (href) =>
    `text-sm tracking-wide transition-colors ${
      href === "/blog" && pathname.startsWith("/blog")
        ? "text-black font-medium"
        : "text-black/70 hover:text-black"
    }`;

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-white/85 backdrop-blur-xl border-b hairline"
          : "bg-transparent"
      }`}
    >
      {isHome && <FollowBanner />}
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="h-28 lg:h-36 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-3">
            <img
              src={LOGO}
              alt="Anderston Group"
              className="h-20 lg:h-28 w-64 lg:w-[22rem] object-cover object-center"
              style={{ mixBlendMode: "multiply" }}
            />
          </Link>

          <nav className="hidden lg:flex items-center gap-9">
            {links.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={linkCls(l.href)}
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <Link
              to="/#contact"
              data-testid="nav-cta-start"
              className="hidden lg:inline-flex items-center px-5 py-2.5 text-sm font-medium bg-[#0A0A0A] text-white hover:bg-black/85 transition-colors"
            >
              Start a project →
            </Link>
            <button
              data-testid="nav-mobile-toggle"
              onClick={() => setOpen((v) => !v)}
              className="lg:hidden inline-flex items-center justify-center w-10 h-10 border hairline"
              aria-label="Toggle menu"
            >
              {open ? <X size={18} /> : <Menu size={18} />}
            </button>
          </div>
        </div>
      </div>

      {/* mobile */}
      {open && (
        <div
          data-testid="mobile-menu"
          className="lg:hidden bg-white border-t hairline"
        >
          <div className="px-6 py-5 flex flex-col gap-4">
            {links.map((l) => (
              <Link
                key={l.href}
                to={l.href}
                onClick={() => setOpen(false)}
                data-testid={`mobile-link-${l.label.toLowerCase()}`}
                className="text-base text-black/80"
              >
                {l.label}
              </Link>
            ))}
            <Link
              to="/#contact"
              onClick={() => setOpen(false)}
              data-testid="mobile-cta-start"
              className="mt-2 inline-flex items-center justify-center px-5 py-3 text-sm font-medium bg-[#0A0A0A] text-white"
            >
              Start a project →
            </Link>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
